import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { MapControl, ControlPosition } from '@vis.gl/react-google-maps'
import PropTypes from 'prop-types'
import React from 'react'

import DriverDatePicker from './DriverDatePicker'
import FitBoundsControl from './FitBoundsControl'
import MapLayerToggles from './MapLayerToggles'
import MapStyleThumbnail from './MapStyleThumbnail'
import MarkerOffsetControl from './MarkerOffsetControl'

// The canonical map-control layout, shared by every map surface so they look
// and sit identically (mirrors the master board):
//   TOP_LEFT     layer toggles + driver-path date picker (when active)
//   LEFT_BOTTOM  marker offset / label-spacing sliders, then bottomLeftFooter
//                (a surface-specific readout, e.g. the change-container map's
//                lat/long fields) below them
//   RIGHT_TOP    basemap thumbnail, then the fit-bounds button below it
//                (the topmost of the two drops below the route A | All | route B
//                toggle that shares this corner on the two-route maps)
// RIGHT_TOP, not TOP_RIGHT: the TOP_* positions flow their controls in a row
// (right-to-left), so a second control lands BESIDE the first rather than under
// it. Only the *_TOP positions stack vertically.
// The basemap thumbnail sits with fit-bounds because both change how you are
// looking at the map, while the sliders change how the markers are drawn;
// grouping by that split also lets the sliders fall into the bottom-left corner
// instead of floating a thumbnail's height above it.
// LEFT_BOTTOM (not the right) keeps the sliders clear of the Intercom launcher,
// which sits bottom-right. Each surface passes only the controls it supports;
// the rest are omitted, so the layout stays consistent everywhere.
// Column + right-aligned so the driver-path date picker sits directly BELOW the
// (rightmost) Driver-path toggle — paired with it, and clear of the two-route
// dialogs' top-right route-layer toggle (zIndex appBar) that otherwise overlaps
// and swallows its clicks when the picker trails the toggle row.
const TOP_LEFT_SX = { mt: 1, ml: 1, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }
// Fixed width so the offset / container-offset / label-spacing sliders all line
// up at the same width regardless of label length.
const SLIDER_SX = { mt: 0, mr: 0, width: 180 }
const getBottomLeftSx = bottomMargin => ({ mb: bottomMargin, ml: 1 })
// Matches FitBoundsControl's own 10px inset. The thumbnail is the topmost
// control, so it carries the route-toggle clearance on the two-route maps, and
// leaves the gap below to the fit-bounds button's own margin.
const getTopRightBasemapSx = (clearancePx, hasFitBounds) => ({
  mr: '10px',
  mt: clearancePx ? `${clearancePx}px` : '10px',
  mb: hasFitBounds ? 0 : '10px',
})
// Top margin that drops the fit-bounds button clear of the route-layer toggle (which sits at top:8, ~32px tall) on the two-route maps.
const ROUTE_TOGGLE_CLEARANCE_PX = 52

const MapControlsCluster = ({
  layerItems,
  activeLayers = [],
  onLayersChange,
  showDriverDate = false,
  driverDateValue = null,
  onDriverDateChange,
  maxDriverDate,
  driverAccentColor,
  isSatellite = false,
  onBasemapToggle,
  basemapBaseStyle = 'roadmap',
  offsetValue,
  onOffsetChange,
  showContainerOffset = false,
  containerOffsetValue,
  onContainerOffsetChange,
  showLabelSpacing = false,
  labelSpacingValue,
  onLabelSpacingChange,
  bottomMargin = 2,
  onFitBounds,
  // Surfaces that move their zoom control off the default corner pass the matching one
  // here, so fit-bounds stays grouped with the +/- buttons rather than stranded.
  fitBoundsPosition = ControlPosition.RIGHT_TOP,
  fitBoundsBelowToggle = false,
  bottomLeftFooter = null,
}) => {
  const clearancePx = fitBoundsBelowToggle ? ROUTE_TOGGLE_CLEARANCE_PX : 0
  // Only when fit-bounds shares the thumbnail's corner does the thumbnail hand its
  // bottom margin over to the button below it.
  const hasFitBoundsBelowThumbnail = Boolean(onFitBounds) && fitBoundsPosition === ControlPosition.RIGHT_TOP
  const hasLayerToggles = Boolean(layerItems?.length && onLayersChange)
  const hasOffset = Boolean(onOffsetChange)
  const hasContainerOffset = Boolean(showContainerOffset && onContainerOffsetChange)
  return (
    <>
      {(hasLayerToggles || showDriverDate) && (
        <MapControl position={ControlPosition.TOP_LEFT}>
          <Box sx={TOP_LEFT_SX}>
            {hasLayerToggles && <MapLayerToggles items={layerItems} value={activeLayers} onChange={onLayersChange} />}
            {showDriverDate && (
              <DriverDatePicker
                accentColor={driverAccentColor}
                maxDate={maxDriverDate}
                value={driverDateValue}
                onChange={onDriverDateChange}
              />
            )}
          </Box>
        </MapControl>
      )}
      {(hasOffset || hasContainerOffset || showLabelSpacing || bottomLeftFooter) && (
        <MapControl position={ControlPosition.LEFT_BOTTOM}>
          <Stack alignItems="flex-start" spacing={0.5} sx={getBottomLeftSx(bottomMargin)}>
            {hasOffset && <MarkerOffsetControl sx={SLIDER_SX} value={offsetValue} onChange={onOffsetChange} />}
            {hasContainerOffset && (
              <MarkerOffsetControl
                label="Container offset"
                sx={SLIDER_SX}
                value={containerOffsetValue}
                onChange={onContainerOffsetChange}
              />
            )}
            {showLabelSpacing && (
              <MarkerOffsetControl
                label="Label spacing"
                max={6}
                min={0}
                suffix=""
                sx={SLIDER_SX}
                value={labelSpacingValue}
                onChange={onLabelSpacingChange}
              />
            )}
            {/* Last child rather than its own MapControl: controls in a
                LEFT_BOTTOM stack flow upwards, so a second one would land above
                the sliders, not below them. */}
            {bottomLeftFooter}
          </Stack>
        </MapControl>
      )}
      {/* Before FitBoundsControl so Google stacks the thumbnail above the button. */}
      {onBasemapToggle && (
        <MapControl position={ControlPosition.RIGHT_TOP}>
          <Box sx={getTopRightBasemapSx(clearancePx, hasFitBoundsBelowThumbnail)}>
            <MapStyleThumbnail baseStyle={basemapBaseStyle} isSatellite={isSatellite} onToggle={onBasemapToggle} />
          </Box>
        </MapControl>
      )}
      {onFitBounds && (
        // The clearance belongs to whichever control is on top, so it only
        // applies here when there is no thumbnail above to carry it.
        <FitBoundsControl fitBounds={onFitBounds} position={fitBoundsPosition} topOffset={onBasemapToggle ? 0 : clearancePx} />
      )}
    </>
  )
}

MapControlsCluster.propTypes = {
  layerItems: PropTypes.arrayOf(PropTypes.object),
  activeLayers: PropTypes.arrayOf(PropTypes.string),
  onLayersChange: PropTypes.func,
  showDriverDate: PropTypes.bool,
  driverDateValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  onDriverDateChange: PropTypes.func,
  maxDriverDate: PropTypes.instanceOf(Date),
  driverAccentColor: PropTypes.string,
  isSatellite: PropTypes.bool,
  onBasemapToggle: PropTypes.func,
  basemapBaseStyle: PropTypes.oneOf(['dark', 'roadmap']),
  offsetValue: PropTypes.number,
  onOffsetChange: PropTypes.func,
  showContainerOffset: PropTypes.bool,
  containerOffsetValue: PropTypes.number,
  onContainerOffsetChange: PropTypes.func,
  showLabelSpacing: PropTypes.bool,
  labelSpacingValue: PropTypes.number,
  onLabelSpacingChange: PropTypes.func,
  bottomMargin: PropTypes.number,
  onFitBounds: PropTypes.func,
  fitBoundsPosition: PropTypes.number,
  fitBoundsBelowToggle: PropTypes.bool,
  bottomLeftFooter: PropTypes.node,
}

export default MapControlsCluster
